/** The select value for "this field is not in the sheet". */
export const UNMAPPED = '';

interface ColumnOption {
  value: string;
  label: string;
}

/** `0 → A`, `25 → Z`, `26 → AA` — Excel's own column names. */
export function columnLetter(index: number): string {
  let letters = '';
  let remaining = index;
  while (remaining >= 0) {
    letters = String.fromCharCode(65 + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26) - 1;
  }
  return letters;
}

/**
 * One option per sheet column, after a leading "none" option.
 *
 * Column letters, because that is what the operator sees in Excel's own column
 * gutter and what they will scroll to if the mapping is wrong.
 */
export function buildColumnOptions(headers: readonly string[], noneLabel: string): ColumnOption[] {
  return [
    { value: UNMAPPED, label: noneLabel },
    ...headers.map((header, index) => ({
      value: String(index),
      label: `${columnLetter(index)} · ${header || '(no heading)'}`,
    })),
  ];
}

/**
 * The column options for one field's select. A column already spoken for by
 * another field is shown but marked, rather than hidden: hiding it makes a
 * mis-mapping impossible to diagnose from the screen it happened on.
 */
export function markUsedColumns(
  options: readonly ColumnOption[],
  mapping: Readonly<Record<string, number>>,
  fieldKey: string,
): ColumnOption[] {
  const used = new Set(Object.values(mapping));
  const own = String(mapping[fieldKey]);
  return options.map((option) => ({
    ...option,
    label:
      option.value !== UNMAPPED && option.value !== own && used.has(Number(option.value))
        ? `${option.label} (already used)`
        : option.label,
  }));
}

/** The mapping with one field pointed at a column, or removed for `UNMAPPED`. */
export function remapField(
  mapping: Readonly<Record<string, number>>,
  fieldKey: string,
  choice: string,
): Record<string, number> {
  const next = { ...mapping };
  if (choice === UNMAPPED) delete next[fieldKey];
  else next[fieldKey] = Number(choice);
  return next;
}
